import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Badge,
  BadgeCreatedEnvelope,
  BadgeDeletedEnvelope,
  BadgeQuery,
  BadgeUpdatedEnvelope,
  BadgesQueryBuilder,
  MoveBadgeOptions,
  QueryBadgesResponse,
  UpdateBadge,
  UpdateBadgeValidationErrors,
  UpdateBadgesDisplayOrderResponse,
  createBadge as universalCreateBadge,
  deleteBadge as universalDeleteBadge,
  getBadge as universalGetBadge,
  moveBadge as universalMoveBadge,
  queryBadges as universalQueryBadges,
  typedQueryBadges as universalTypedQueryBadges,
  updateBadge as universalUpdateBadge,
  updateBadgesDisplayOrder as universalUpdateBadgesDisplayOrder,
} from './badges-v4-badge-badges-v-2.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function createBadge(httpClient: HttpClient): CreateBadgeSignature {
  return (
    badge: NonNullablePaths<Badge, `backgroundColor` | `textColor` | `title`, 2>
  ) =>
    universalCreateBadge(
      badge,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBadgeSignature {
  /**
   * Creates a badge.
   * @param - Badge to create.
   * @returns Created badge.
   */
  (
    badge: NonNullablePaths<Badge, `backgroundColor` | `textColor` | `title`, 2>
  ): Promise<Badge>;
}

export function getBadge(httpClient: HttpClient): GetBadgeSignature {
  return (badgeId: string) =>
    universalGetBadge(
      badgeId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBadgeSignature {
  /**
   * Retrieves a badge by ID.
   * @param - Badge ID.
   * @returns Retrieved badge.
   */
  (badgeId: string): Promise<Badge>;
}

export function queryBadges(httpClient: HttpClient): QueryBadgesSignature {
  return () =>
    universalQueryBadges(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBadgesSignature {
  /**
   * Creates a query to retrieve a list of badges.
   *
   * The `queryBadges()` method builds a query to retrieve a list of badges and returns a `BadgesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `BadgesQueryBuilder` methods onto the query. `BadgesQueryBuilder` methods enable you to sort, filter, and control the results `queryBadges()` returns.
   *
   * `queryBadges()` runs with these `BadgesQueryBuilder` defaults, which you can override:
   *
   * - limit(50)
   * - descending("_createdDate")
   *
   * The methods that are chained to `queryBadges()` are applied in the order they're called. For example, if you apply ascending('title') and then descending('id'), the results are sorted first by the title, and then, if there are multiple results with the same title, the items are sorted by ID.
   */
  (): BadgesQueryBuilder;
}

export function typedQueryBadges(
  httpClient: HttpClient
): TypedQueryBadgesSignature {
  return (query: BadgeQuery) =>
    universalTypedQueryBadges(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryBadgesSignature {
  /** */
  (query: BadgeQuery): Promise<
    NonNullablePaths<QueryBadgesResponse, `badges`, 2>
  >;
}

export function updateBadge(httpClient: HttpClient): UpdateBadgeSignature {
  return (_id: string, badge: UpdateBadge) =>
    universalUpdateBadge(
      _id,
      badge,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBadgeSignature {
  /**
   * Updates a badge.
   * @param - Badge ID.
   * @returns Updated badge with incremented revision and updated timestamp.
   */
  (_id: string, badge: UpdateBadge): Promise<
    Badge & {
      __validationErrorsType?: UpdateBadgeValidationErrors;
    }
  >;
}

export function deleteBadge(httpClient: HttpClient): DeleteBadgeSignature {
  return (badgeId: string) =>
    universalDeleteBadge(
      badgeId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBadgeSignature {
  /**
   * Deletes a badge.
   *
   * Deleting a badge will remove it from all members who currently have it assigned.
   * @param - Badge ID.
   */
  (badgeId: string): Promise<void>;
}

export function updateBadgesDisplayOrder(
  httpClient: HttpClient
): UpdateBadgesDisplayOrderSignature {
  return (ids: string[]) =>
    universalUpdateBadgesDisplayOrder(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBadgesDisplayOrderSignature {
  /**
   * Updates the display order of badges.
   *
   * Provide an ordered array of badge IDs to set their display sequence.
   *
   * This affects how badges appear to site members and in the site dashboard.
   * @param - Ordered list of badge IDs.
   * @deprecated
   */
  (ids: string[]): Promise<
    NonNullablePaths<UpdateBadgesDisplayOrderResponse, `badges`, 2>
  >;
}

export function moveBadge(httpClient: HttpClient): MoveBadgeSignature {
  return (badgeId: string, options?: MoveBadgeOptions) =>
    universalMoveBadge(
      badgeId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MoveBadgeSignature {
  /**
   * Moves a badge to a specific position in the display order.
   *
   * You can position a badge first, last, or immediately after another badge.
   *
   * This affects how badges appear to site members and in the site dashboard.
   * @param - ID of the badge to move.
   */
  (badgeId: string, options?: MoveBadgeOptions): Promise<void>;
}

export const onBadgeCreated = EventDefinition(
  'wix.badges.v4.badge_created',
  true,
  (event: BadgeCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.icon' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BadgeCreatedEnvelope>();
export const onBadgeDeleted = EventDefinition(
  'wix.badges.v4.badge_deleted',
  true,
  (event: BadgeDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.icon' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BadgeDeletedEnvelope>();
export const onBadgeUpdated = EventDefinition(
  'wix.badges.v4.badge_updated',
  true,
  (event: BadgeUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.icon' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BadgeUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Badge,
  BadgeCreatedEnvelope,
  BadgeDeletedEnvelope,
  BadgeQuerySpec,
  BadgeUpdatedEnvelope,
  BadgesQueryBuilder,
  BadgesQueryResult,
  BaseEventMetadata,
  CreateBadgeRequest,
  CreateBadgeResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteBadgeRequest,
  DeleteBadgeResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetBadgeRequest,
  GetBadgeResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  MigrationBadge,
  MigrationBadgeCreated,
  MigrationBadgeUpdated,
  MoveBadgeOptions,
  MoveBadgeRequest,
  MoveBadgeResponse,
  Paging,
  PagingMetadata,
  Position,
  QueryBadgesLegacyRequest,
  QueryBadgesLegacyResponse,
  QueryBadgesRequest,
  QueryBadgesResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UpdateBadge,
  UpdateBadgeRequest,
  UpdateBadgeResponse,
  UpdateBadgesDisplayOrderRequest,
  UpdateBadgesDisplayOrderResponse,
  WebhookIdentityType,
  utils,
} from './badges-v4-badge-badges-v-2.universal.js';
